import {
  doc, runTransaction, collection, addDoc, serverTimestamp,
  deleteDoc, updateDoc, writeBatch, query, where,
  orderBy, getDocs, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export const TRANSACTION_TYPES = {
  restock:    { label: 'Restock',    direction: +1, color: '#16a34a', bg: '#f0fdf4', description: 'Received new stock from supplier' },
  sale:       { label: 'Sale',       direction: -1, color: '#3b82f6', bg: '#eff6ff', description: 'Sold to customer' },
  writeoff:   { label: 'Write-off',  direction: -1, color: '#f97316', bg: '#fff7ed', description: 'Expired, damaged, or lost stock' },
  return:     { label: 'Return',     direction: +1, color: '#8b5cf6', bg: '#f5f3ff', description: 'Customer return' },
  adjustment: { label: 'Adjustment', direction:  0, color: '#64748b', bg: '#f8fafc', description: 'Manual correction of stock count' },
  created:    { label: 'Created',    direction: +1, color: '#0ea5e9', bg: '#f0f9ff', description: 'Product added to inventory' },
  delete:     { label: 'Deleted',    direction: -1, color: '#dc2626', bg: '#fef2f2', description: 'Product removed from inventory' },
};

// ← added `note = ''` parameter
export async function updateStock(product, quantity, transactionType, user, orgId, note = '') {
  const productRef = doc(db, 'products', product.id);

  if (!quantity || isNaN(quantity) || Number(quantity) === 0)
    throw new Error('Please enter a valid quantity.');

  const txn = TRANSACTION_TYPES[transactionType];
  if (!txn) throw new Error('Invalid transaction type.');

  // ← moved BEFORE runTransaction so it throws synchronously-ish
  // (actually fine inside too, but this matches test ordering expectations)
  const rawQty = parseInt(quantity);

  if (transactionType !== 'adjustment' && rawQty <= 0)
    throw new Error('Quantity must be a positive number.');

  await runTransaction(db, async (transaction) => {
    const productDoc = await transaction.get(productRef);
    if (!productDoc.exists()) throw new Error('Product not found.');

    const currentStock = productDoc.data().stock;

    const delta = transactionType === 'adjustment'
      ? rawQty
      : txn.direction * rawQty;

    if (delta < 0 && currentStock + delta < 0)
      throw new Error(
        `Cannot reduce stock by ${Math.abs(delta)}. Current stock is only ${currentStock}.`
      );

    transaction.update(productRef, { stock: currentStock + delta });

    // ← build log entry, conditionally include notes
    const trimmedNote = note?.trim();
    const logEntry = {
      orgId,
      productId:    product.id,
      productName:  product.name,
      productGroup: product.group,
      userId:       user.uid,
      userName:     user.displayName || user.email,
      transactionType,
      delta,
      quantity:     Math.abs(delta),
      oldStock:     currentStock,
      newStock:     currentStock + delta,
      timestamp:    serverTimestamp(),
    };
    if (trimmedNote) logEntry.notes = trimmedNote;  // ← only added if non-empty

    await addDoc(collection(db, 'activityLog'), logEntry);
  });
}


export async function updateProductDetails(productId, updates, orgId) {
  await updateDoc(doc(db, 'products', productId), updates);

  if (updates.name && orgId) {
    try {
      await syncProductNameInLogs(productId, updates.name, orgId);
    } catch (err) {
      console.error('syncProductNameInLogs failed:', err.message, err.code);
    }
  }
}

export async function updateMinStock(productId, minStock) {
  await updateDoc(doc(db, 'products', productId), { minStock: Number(minStock) });
}

export async function deleteProduct(product, user, orgId) {
  await deleteDoc(doc(db, 'products', product.id));
  await addDoc(collection(db, 'activityLog'), {
    orgId,
    productId:    product.id,
    productName:  product.name,
    productGroup: product.group,
    userId:       user.uid,
    userName:     user.displayName || user.email,
    transactionType: 'delete',
    delta:    -product.stock,
    quantity:  product.stock,
    oldStock:  product.stock,
    newStock:  0,
    timestamp: serverTimestamp(),
  });
}

export async function logProductCreated(product, user, orgId) {
  await addDoc(collection(db, 'activityLog'), {
    orgId,
    productId:    product.id,
    productName:  product.name,
    productGroup: product.group,
    userId:       user.uid,
    userName:     user.displayName || user.email,
    transactionType: 'created',
    delta:    product.stock,
    quantity:  product.stock,
    oldStock:  0,
    newStock:  product.stock,
    timestamp: serverTimestamp(),
  });
}

export async function deleteActivityLog(logId) {
  await deleteDoc(doc(db, 'activityLog', logId));
}

export async function deleteActivityLogs(orgId, olderThanDate) {
  const q = query(
    collection(db, 'activityLog'),
    where('orgId', '==', orgId),
    where('timestamp', '<=', Timestamp.fromDate(olderThanDate)),
    orderBy('timestamp', 'desc')
  );

  const snap = await getDocs(q);
  if (snap.empty) return 0;

  const allDocs = snap.docs;
  const chunks = [];
  for (let i = 0; i < allDocs.length; i += 499) {
    chunks.push(allDocs.slice(i, i + 499));
  }

  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }

  return allDocs.length;
}

export async function syncProductNameInLogs(productId, newName, orgId) {
  // Query uses the existing composite index: orgId ASC + productId ASC + timestamp DESC
  const q = query(
    collection(db, 'activityLog'),
    where('orgId',     '==', orgId),
    where('productId', '==', productId)
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  // Batch in chunks of 499 to respect Firestore limit
  const chunks = [];
  for (let i = 0; i < snap.docs.length; i += 499)
    chunks.push(snap.docs.slice(i, i + 499));

  for (const chunk of chunks) {
    const batch = writeBatch(db);
    chunk.forEach(d => batch.update(d.ref, { productName: newName }));
    await batch.commit();
  }
}